import axios, { AxiosResponse, AxiosError } from "axios";
import { signOut } from "next-auth/react";
import { shouldHandle401 } from "@/utils/auth-context";
import { isJwtExpired } from "@/app/api/proxy/jwtUtils";

const isServer = typeof window === 'undefined';

const getBaseURL = () => {
    if (isServer) {
        return (process.env.BACKEND_API_URL || process.env.NEXT_PUBLIC_API_URL || "").replace(/\/$/, "");
    }
    return "/api/proxy";
};

export const axiosClient = axios.create({
    baseURL: getBaseURL(),
});

// Add request interceptor to inject api key server-side and sanitize dead tokens on public client pages
axiosClient.interceptors.request.use((config) => {
    if (isServer) {
        const apiKey = process.env.BACKEND_API_KEY || "";
        if (apiKey && config.headers) {
            if (typeof config.headers.set === 'function') {
                config.headers.set('x-api-key', apiKey);
            } else {
                (config.headers as any)['x-api-key'] = apiKey;
            }
        }
    } else if (typeof window !== 'undefined' && !shouldHandle401(window.location.pathname)) {
        const headers = config.headers;
        if (headers) {
            const rawToken = typeof headers.get === 'function'
                ? (headers.get('access-token') || headers.get('authorization'))
                : ((headers as any)['access-token'] || (headers as any)['Authorization'] || (headers as any)['authorization']);

            if (typeof rawToken === 'string' && (rawToken === '' || rawToken === 'null' || rawToken === 'undefined' || isJwtExpired(rawToken))) {
                if (typeof headers.delete === 'function') {
                    headers.delete('access-token');
                    headers.delete('Authorization');
                    headers.delete('authorization');
                } else {
                    delete (headers as any)['access-token'];
                    delete (headers as any)['Authorization'];
                    delete (headers as any)['authorization'];
                }
            }
        }
    }
    return config;
});

// Intercept 401 needActivation responses → redirect to activation page
axiosClient.interceptors.response.use(
    (response) => response,
    (error) => {
        const requestUrl = String(error?.config?.url || '');
        const shouldSkipActivationRedirect =
            requestUrl.includes('/v1/projects/minimap') ||
            requestUrl.includes('/v1/projects/scene/') ||
            requestUrl.includes('/v1/projects/group-scene/');

        if (
            typeof window !== 'undefined' &&
            error?.response?.status === 401 &&
            error?.response?.data?.needActivation === true &&
            !window.location.pathname.startsWith('/activate') &&
            !shouldSkipActivationRedirect
        ) {
            // Save current URL so we can redirect back after activation
            sessionStorage.setItem('activation_return_url', window.location.href);
            window.location.href = '/activate';
            return new Promise(() => { }); // Halt further processing
        }
        
        // Auto-logout on 401 if it's not a needActivation error
        if (requestUrl.includes('/tts') || requestUrl.includes('/budget/status')) {
            return Promise.reject(error);
        }

        if (
            typeof window !== 'undefined' &&
            error?.response?.status === 401 &&
            error?.response?.data?.needActivation !== true
        ) {
            // [FIX PHASE A]: Only handle 401 auto-redirect in Admin/Project Admin context
            if (!shouldHandle401(window.location.pathname)) {
                const originalRequest = error.config;
                if (originalRequest && !originalRequest._retryWithoutToken) {
                    originalRequest._retryWithoutToken = true;
                    if (originalRequest.headers) {
                        if (typeof originalRequest.headers.delete === 'function') {
                            originalRequest.headers.delete('access-token');
                            originalRequest.headers.delete('Authorization');
                            originalRequest.headers.delete('authorization');
                        } else {
                            delete (originalRequest.headers as any)['access-token'];
                            delete (originalRequest.headers as any)['Authorization'];
                            delete (originalRequest.headers as any)['authorization'];
                        }
                    }
                    return axiosClient(originalRequest);
                }
                return Promise.reject(error);
            }

            if (!(window as any).__isLoggingOut) {
                (window as any).__isLoggingOut = true;
                window.dispatchEvent(new CustomEvent('session_expired_event'));

                // Calculate dynamic login path based on current route
                const pathname = window.location.pathname;
                const segments = pathname.split('/').filter(Boolean);
                const adminIndex = segments.indexOf('admin');

                let loginUrl = '/login'; // default fallback
                if (adminIndex !== -1) {
                    loginUrl = `/${segments.slice(0, adminIndex + 1).join('/')}/login`;
                }

                setTimeout(async () => {
                    await signOut({ redirect: false });
                    (window as any).__isLoggingOut = false;
                    window.location.href = loginUrl + '?expired=true';
                }, 2500);
            }
        }
        
        return Promise.reject(error);
    }
);

// Add response interceptor to handle token refresh

// Utility to handle responses consistently
async function handleRequest<T>(promise: Promise<AxiosResponse<T>>): Promise<AxiosResponse<T>> {
    try {
        const response = await promise; // Successful request
        return response; // Return the full Axios response
    } catch (error) {
        const axiosError = error as AxiosError;
        if (axiosError.response) {
            // Server responded with a non-2xx status code
            return axiosError.response as AxiosResponse<T>; // Return the error response
        }
        console.error("Network or unexpected error:", axiosError.message, {
            method: axiosError.config?.method,
            url: axiosError.config?.url,
            baseURL: axiosError.config?.baseURL,
            params: axiosError.config?.params,
        });
        throw new Error("Network error or no response from server.");
    }
}

interface RequestParams {
    endpoint: string;
    headers?: Record<string, string>;
    signal?: AbortSignal;
}

interface BodyRequestParams extends RequestParams {
    body?: any;
}

interface StreamRequestParams extends BodyRequestParams {
    onStream?: (progressEvent: any) => void;
}

export function baseGet<T>({ endpoint, headers, signal }: RequestParams): Promise<AxiosResponse<T>> {
    return handleRequest<T>(axiosClient.get(endpoint, { headers, signal }));
}

export function basePost<T>({ endpoint, headers, body }: BodyRequestParams): Promise<AxiosResponse<T>> {
    return handleRequest<T>(axiosClient.post(endpoint, body, { headers }));
}

// Handle Stream request (onStream is passed from outside)
export async function basePostStream<T>({ endpoint, headers, body, onStream }: StreamRequestParams): Promise<AxiosResponse<T>> {
    return handleRequest<T>(
        axios({
            method: "post",
            headers: isServer ? { ...headers, "x-api-key": process.env.BACKEND_API_KEY || "" } : headers,
            url: getBaseURL() + endpoint,
            data: body,
            responseType: "stream",
            onDownloadProgress: onStream,
        })
    );
}

export function basePut<T>({ endpoint, headers, body }: BodyRequestParams): Promise<AxiosResponse<T>> {
    return handleRequest<T>(axiosClient.put(endpoint, body, { headers }));
}

export function basePatch<T>({ endpoint, headers, body }: BodyRequestParams): Promise<AxiosResponse<T>> {
    return handleRequest<T>(axiosClient.patch(endpoint, body, { headers }));
}

export function baseDelete<T>({ endpoint, headers }: RequestParams): Promise<AxiosResponse<T>> {
    return handleRequest<T>(axiosClient.delete(endpoint, { headers }));
}
